import { Label } from '@optools/components/ui/label';
import { setHours, setMinutes, setSeconds } from 'date-fns';
import { Clock } from 'lucide-react';
import React, { useMemo } from 'react';

import { DateTimePickerProps } from './date-time-picker';
import { TimePickerInput, TimePickerType } from './time-picker-input';

function setDateByInputType(date: Date, value: string, type: TimePickerType) {
  const parsedValue = parseInt(value, 10);
  if (parsedValue < 0) {
    return date;
  }

  if (type === 'hours' && parsedValue < 24) {
    return setHours(date, parsedValue);
  } else if (type === 'minutes' && parsedValue < 60) {
    return setMinutes(date, parsedValue);
  } else if (type === 'seconds' && parsedValue < 60) {
    return setSeconds(date, parsedValue);
  } else {
    return date;
  }
}

export function TimePicker({ date, setDate }: DateTimePickerProps) {
  const hourRef = React.useRef<HTMLInputElement>(null);
  const minuteRef = React.useRef<HTMLInputElement>(null);
  const secondRef = React.useRef<HTMLInputElement>(null);

  const { hours, minutes, seconds } = useMemo(
    () => ({
      hours: date?.getHours().toString(),
      minutes: date?.getMinutes().toString(),
      seconds: date?.getSeconds().toString(),
    }),
    [date],
  );

  const setValueHandler = (value: string, type: TimePickerType) => {
    setDate(setDateByInputType(date, value, type));
  };

  return (
    <div className="flex items-end gap-2">
      <div className="flex flex-col gap-1 text-center">
        <Label className="text-xs" htmlFor="hours">
          Hours
        </Label>
        <TimePickerInput
          ref={hourRef}
          id="hours"
          setValue={setValueHandler}
          time={hours}
          timeType="hours"
          onFocusRight={() => minuteRef.current?.focus()}
        />
      </div>
      <div className="flex flex-col gap-1 text-center">
        <Label className="text-xs" htmlFor="minutes">
          Minutes
        </Label>
        <TimePickerInput
          ref={minuteRef}
          id="minutes"
          setValue={setValueHandler}
          time={minutes}
          timeType="minutes"
          onFocusLeft={() => hourRef.current?.focus()}
          onFocusRight={() => secondRef.current?.focus()}
        />
      </div>
      <div className="flex flex-col gap-1 text-center">
        <Label className="text-xs" htmlFor="seconds">
          Seconds
        </Label>
        <TimePickerInput
          ref={secondRef}
          id="seconds"
          setValue={setValueHandler}
          time={seconds}
          timeType="seconds"
          onFocusLeft={() => minuteRef.current?.focus()}
        />
      </div>
      <div className="flex h-10 items-center">
        <Clock className="ml-2 h-4 w-4" />
      </div>
    </div>
  );
}
